import { Injectable } from '@angular/core';
import {
  injectMutation,
  injectQuery,
  QueryKey,
} from '@tanstack/angular-query-experimental';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { UserInfo } from '../models';
import { BaseApiService } from './base.api.service';

@Injectable({ providedIn: 'root' })
export class UserService extends BaseApiService {
  private userInfoKey: QueryKey = ['myuserinfo'];
  private accesTokenInformationKey: QueryKey = ['accessTokenInformations'];

  getUserInfos() {
    return injectQuery(() => ({
      queryKey: this.userInfoKey,
      queryFn: async () => {
        try {
          const userInfo = await lastValueFrom(
            this.httpClient.get<UserInfo>(`${this.baseUrl}/connect/userinfo`, {
              withCredentials: true,
            })
          );

          if (userInfo && this.isTokenExpired(userInfo.tokenExpirationDate)) {
            // Ne pas rediriger automatiquement si on est sur la landing page
            if (this.router.url.startsWith('/app')) {
              this.authService.redirectToLogin();
            }
          }

          return userInfo;
        } catch (error: any) {
          this.handleError(error);
          throw error;
        }
      },
      staleTime: 30 * 60 * 1000, // Cache de 30 min
      retry: false,
    }));
  }

  updateUserInfos = injectMutation(() => ({
    retry: false,
    mutationFn: async (args: { email: string; phoneNumber: string }) => {
      const response = await firstValueFrom(
        this.httpClient.put<UserInfo>(`${this.baseUrl}/api/users`, {
          email: args.email,
          phoneNumber: args.phoneNumber
        }, {
          withCredentials: true
        })
      );
      return response;
    },
    onSuccess: () => {
      this.queryClient.invalidateQueries({ queryKey: this.userInfoKey });
    },
    onMutate: async () => {
      await this.queryClient.cancelQueries({
        queryKey: this.userInfoKey,
      });
    },
    onSettled: () => {
      this.queryClient.invalidateQueries({ queryKey: this.userInfoKey });
    }
  }));

  deleteAccount = injectMutation(() => ({
    mutationFn: async () => {
      return lastValueFrom(
        this.httpClient.delete(`${this.baseUrl}/api/account/delete`, { withCredentials: true })
      );
    }
  }));

  reloadAccessTokenInformations = injectMutation(() => ({
    mutationFn: async () => {
      try {
        return await lastValueFrom(
          this.httpClient.get(`${this.baseUrl}/connect/invalidateAccessToken`, {
            withCredentials: true,
          })
        );
      } catch (error: any) {
        this.handleError(error);
        throw error;
      }
    },
    onSuccess: () => {
      this.queryClient.invalidateQueries({ queryKey: this.userInfoKey });
      this.queryClient.refetchQueries({
        queryKey: this.userInfoKey,
      });
    },
  }));

  /** Forcer la mise à jour du cache */
  refetchUser() {
    this.queryClient.invalidateQueries({ queryKey: this.userInfoKey });
  }

  /** Déconnexion */
  logout() {
    this.queryClient.removeQueries({ queryKey: this.userInfoKey });
    this.httpClient
      .get(`${this.baseUrl}/clear/cookies`, {
        withCredentials: true,
      })
      .subscribe({
        next: () => {
          var redirectUri = window.location.origin + '/auth/logoutcallback';
          window.location.href = `${this.baseUrl}/clear/session?returnUrl=${redirectUri}`;
        },
        error: (error) => {
          console.error('Error during logout:', error);
          this.handleError(error);
        },
      });
  }
}
